import { useBlocker } from "react-router";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { showBlockNavigationModal } from "./BlockNavigationModal";

type UseBlockNavigationModalProps = {
  isNavigationBlocked: boolean;
  // When provided, the blocker is skipped if this ref is `true` — use this to
  // allow navigation after a successful submit without reading the ref during render.
  skipBlockRef?: React.RefObject<boolean>;
  title?: string;
  description?: string;
  onConfirm?: () => void;
};

const useBlockNavigationModal = ({
  isNavigationBlocked,
  skipBlockRef,
  title,
  description,
  onConfirm,
}: UseBlockNavigationModalProps) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isNavigationBlocked &&
      !skipBlockRef?.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  const blockerRef = useRef(blocker);
  const onConfirmRef = useRef(onConfirm);
  // Guards against resolving the same blocked navigation twice. Confirming the
  // modal hides it, which fires the modal's `onClose` (i.e. cancel) as well —
  // without this flag both `proceed()` and `reset()` would run and React Router
  // would throw "Invalid blocker state transition".
  const isResolvedRef = useRef(false);

  useLayoutEffect(() => {
    blockerRef.current = blocker;
    onConfirmRef.current = onConfirm;
    if (blocker.state === "blocked") {
      isResolvedRef.current = false;
    }
  });

  const onNavigationConfirm = useCallback(() => {
    if (isResolvedRef.current || blockerRef.current.state !== "blocked") {
      return;
    }
    isResolvedRef.current = true;
    onConfirmRef.current?.();
    blockerRef.current.proceed();
  }, []);

  const onNavigationCancel = useCallback(() => {
    if (isResolvedRef.current || blockerRef.current.state !== "blocked") {
      return;
    }
    isResolvedRef.current = true;
    blockerRef.current.reset();
  }, []);

  useEffect(() => {
    if (blocker.state === "blocked")
      showBlockNavigationModal({
        title,
        description,
        onConfirm: onNavigationConfirm,
        onCancel: onNavigationCancel,
      });
  }, [blocker.state, title, description, onNavigationConfirm, onNavigationCancel]);
};

export default useBlockNavigationModal;
